import React, { useEffect, useRef, useState } from "react";
import { Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import SearchInput from "../../../../common/InlineSearch";
import { getRecordListCountDetails } from "../../../../services/UploadRecordsApi";

interface Category {
  datacategory: string;
  totalRecords: number;
  categorywiseTotalPageCount: number;
  keyId: number;
}

interface TooltipData {
  lastUploadedBy: string;
  lastUploadedOn: string;
  totalPages: number;
}

interface Props {
  categories: Category[];
  selected: string | null;
  onSelect: (category: Category) => void;
  sortBySender: boolean;
  caseInfoId: any;
}

const DocumentCategoryList: React.FC<Props> = ({
  categories,
  selected,
  onSelect,
  sortBySender,
  caseInfoId,
}) => {
  const searchInputRef = useRef<any>(null);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const [tooltipData, setTooltipData] = useState<Record<string, TooltipData>>(
    {}
  );
  const [loadingTooltips, setLoadingTooltips] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    searchInputRef.current?.clear();
    setFilteredCategories(categories);
  }, [categories, sortBySender]);

  const handleSearch = (query: string) => {
    if (!query.trim()) {
      setFilteredCategories(categories);
    } else {
      const filtered = categories.filter((cat) =>
        cat.datacategory.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredCategories(filtered);
    }
  };

  const fetchTooltipData = async (cat: Category) => {
    const groupType = sortBySender === false ? 1 : 0;

    if (tooltipData[cat.datacategory] || loadingTooltips[cat.datacategory])
      return;

    setLoadingTooltips((prev) => ({ ...prev, [cat.datacategory]: true }));
    try {
      const listData: any = await getRecordListCountDetails(
        caseInfoId,
        groupType,
        ""
      );

      const matched = listData?.result?.find(
        (r: any) => r.datacategory === cat.datacategory
      );

      const info: TooltipData = {
        lastUploadedBy: matched?.lastUploadedBy || "",
        lastUploadedOn: matched?.lastUploadedOn || "",
        totalPages: matched?.categorywiseTotalPageCount || 0,
      };

      setTooltipData((prev) => ({ ...prev, [cat.datacategory]: info }));
    } catch (err) {
      console.error("Failed to fetch tooltip data", err);
    } finally {
      setLoadingTooltips((prev) => ({ ...prev, [cat.datacategory]: false }));
    }
  };

  return (
    <div className="border-0 mb-2 doctor-detail">
      <div style={{ width: "100%" }} className="gridsearch mb-2 p-2 pb-0">
        <SearchInput
          ref={searchInputRef}
          data={categories}
          searchKeys={["datacategory"]}
          onSearchResult={setFilteredCategories}
          placeholder="Search"
          onSearchQueryChange={handleSearch}
        />
      </div>

      <div className="badges_recordss">
        <ul className="list-unstyled m-0 p-0">
          {filteredCategories?.length > 0 ? (
            filteredCategories.map((cat, index) => {
              const data = tooltipData[cat.datacategory];

              return (
                <OverlayTrigger
                  key={`category-${index}`}
                  placement="top"
                  delay={{ show: 300, hide: 150 }}
                  overlay={
                    <Tooltip id={`tooltip-${index}`}>
                      {loadingTooltips[cat.datacategory] ? (
                        <span>Loading...</span>
                      ) : data ? (
                        <>
                          <div>
                            <strong>Last Uploaded On:</strong>{" "}
                            {`${new Date(
                              data.lastUploadedOn
                            ).toLocaleDateString("en-US")} | ${
                              data.lastUploadedBy
                            }`}
                          </div>
                          <div>
                            <strong>Pages:</strong> {data.totalPages || 0}
                          </div>
                        </>
                      ) : (
                        ""
                      )}
                    </Tooltip>
                  }
                >
                  <li
                    key={`category-${index}`}
                    className={`list-group-item d-flex justify-content-between align-items-center py-2 px-2 cursor-pointer ${
                      selected === cat.datacategory
                        ? "rowSelectedDoctorCheck"
                        : ""
                    }`}
                    onClick={() => onSelect(cat)}
                    onMouseEnter={() => fetchTooltipData(cat)}
                  >
                    <span className="rowSelectedDoctorCheck1">
                      {cat.datacategory}
                    </span>
                    <Badge bg="primary" className="ms-2">
                      {cat.totalRecords}
                    </Badge>
                  </li>
                </OverlayTrigger>
              );
            })
          ) : (
            <div className="text-center no_rec_data ">
              <span className="newThingsNod">
                No {sortBySender ? "Sender" : "Category"} Found
              </span>
            </div>
          )}
        </ul>
      </div>
    </div>
  );
};

export default DocumentCategoryList;
